/**
 * Real SQLite connection: @capacitor-community/sqlite on native (Android/iOS/Electron), the
 * plugin's own jeep-sqlite (IndexedDB-backed wasm SQLite) web fallback in the browser build.
 * Implements the SqlDriver interface engine.ts runs schema.ts's query-builder output against.
 */
import { Capacitor } from '@capacitor/core';
import { CapacitorSQLite, SQLiteConnection } from '@capacitor-community/sqlite';
import type { SqlDriver } from './engine.js';
import { buildCreateTableStatements, buildAddColumnStatements, tableColumnDefs } from './ddl.js';
import { tables } from '@/livestore/schema.js';

interface DbLike {
  execute(sql: string, values?: unknown[]): Promise<unknown>;
  query(sql: string, values?: unknown[]): Promise<{ values?: Record<string, unknown>[] }>;
}

/** Adds any columns schema.ts has declared since this on-disk database was first created (see
 * buildAddColumnStatements's doc comment for why CREATE TABLE IF NOT EXISTS alone isn't enough). */
async function migrateMissingColumns(db: DbLike): Promise<void> {
  for (const [tableName, columns] of Object.entries(tableColumnDefs(tables))) {
    const info = await db.query(`PRAGMA table_info("${tableName}")`);
    const existing = new Set(
      (info.values ?? [])
        .map(row => (typeof row.name === 'string' ? row.name : null))
        .filter((name): name is string => name !== null),
    );
    for (const stmt of buildAddColumnStatements(tableName, existing, columns)) {
      await db.execute(stmt);
    }
  }
}

const DB_NAME = 'punchlog';
const sqlite = new SQLiteConnection(CapacitorSQLite);

let dbPromise: ReturnType<typeof openConnection> | null = null;

async function ensureWebStore(): Promise<void> {
  if (Capacitor.getPlatform() !== 'web') return;
  if (!customElements.get('jeep-sqlite')) {
    // Merely importing this module does NOT register the custom element -- it's a Stencil
    // component that exports an explicit defineCustomElement() function you must call yourself.
    // Without this, customElements.whenDefined('jeep-sqlite') below waits forever (no error, no
    // rejection -- just an unresolved promise), which is why the app got stuck on "Loading…"
    // with zero console errors.
    const mod = await import('jeep-sqlite/dist/components/jeep-sqlite');
    mod.defineCustomElement();
  }
  if (!document.querySelector('jeep-sqlite')) {
    const el = document.createElement('jeep-sqlite');
    // jeep-sqlite's SQLite instance runs entirely in-memory (wasm); without autosave it never
    // flushes to its IndexedDB-backed store at all, so every write silently vanishes on reload --
    // commits and reads both succeed with no error in the same session, since the in-memory
    // instance itself is intact until the page unloads.
    el.setAttribute('autosave', 'true');
    document.body.appendChild(el);
  }
  await customElements.whenDefined('jeep-sqlite');
  await sqlite.initWebStore();
}

async function openConnection() {
  await ensureWebStore();
  const isConn = (await sqlite.isConnection(DB_NAME, false)).result;
  const db = isConn
    ? await sqlite.retrieveConnection(DB_NAME, false)
    : await sqlite.createConnection(DB_NAME, false, 'no-encryption', 1, false);
  await db.open();
  for (const stmt of buildCreateTableStatements(tables)) await db.execute(stmt);
  await migrateMissingColumns(db as unknown as DbLike);
  return db;
}

function getConnection() {
  if (!dbPromise) dbPromise = openConnection();
  return dbPromise;
}

export const capacitorDriver: SqlDriver = {
  async execute(sql, values) {
    const db = await getConnection();
    await db.run(sql, values as unknown[]);
  },
  async query<T = Record<string, unknown>>(sql: string, values: readonly unknown[]) {
    const db = await getConnection();
    const result = await db.query(sql, values as unknown[]);
    const rows = result.values ?? [];
    return rows.filter(row => {
      const ok = typeof row === 'object' && row !== null;
      if (!ok) console.error('capacitorDriver.query: dropped malformed row', row);
      return ok;
    }) as T[];
  },
};

/** Test-only: forces a fresh connection on next use. */
export function _resetConnectionForTests(): void {
  dbPromise = null;
}
